import React from 'react';
import { Pressable, Platform } from 'react-native';
import { Text, View } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import returnIcon from '../utils/returnIcon';
import toTitleCase from '@hooks/toTitleCase';
import parseByDelimiter from '@utils/parseByDelimiter';
import getDaysAgo from '@utils/getDaysAgo';

/**
 * Generates a context-sensitive notification message based on the notification data
 * This is the same function used in NotificationItem to ensure consistent message parsing
 */
const getContextualMessage = (item: any): string => {
  // If there's already a title, use it (unless it's a generic one)
  if (item.title && !item.title.includes('Detected') && !item.title.includes('event in')) {
    return item.title;
  }

  const operationType = item.operationType?.toLowerCase();
  const collection = item.ns?.coll || item.collection;
  const fullDocument = item.fullDocument;
  const documentKey = item.documentKey;
  const postedBy = item.postedBy;
  const dataTitle = item.dataTitle || fullDocument?.dataTitle || fullDocument?.title;
  const notificationType = item.type?.toLowerCase() || '';

  // Extract meaningful fields from fullDocument
  const getDocumentName = () => {
    if (dataTitle) return parseByDelimiter(dataTitle);
    if (fullDocument?.name) return parseByDelimiter(fullDocument.name);
    if (fullDocument?.title) return parseByDelimiter(fullDocument.title);
    if (fullDocument?.dataTitle) return parseByDelimiter(fullDocument.dataTitle);
    if (fullDocument?.email) return fullDocument.email;
    if (fullDocument?.company_name) return parseByDelimiter(fullDocument.company_name);
    return null;
  };

  const documentName = getDocumentName();
  const collectionName = collection ? parseByDelimiter(collection) : null;
  const userName = postedBy
    ? `${toTitleCase(postedBy.profile_information?.name || '')} ${toTitleCase(postedBy.profile_information?.surname || '')}`.trim()
    : null;

  // Handle error notifications
  if (item.error || notificationType.includes('error') || notificationType.includes('failed')) {
    const errorMessage = item.error || item.message || item.body;
    if (documentName) {
      return `${documentName} - ${errorMessage || 'Operation failed'}`;
    }
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} - ${errorMessage || 'Job failed'}`;
    }
    if (errorMessage && !errorMessage.includes('Detected') && !errorMessage.includes('in ') && !errorMessage.includes('event in')) {
      return errorMessage;
    }
    return 'An error occurred';
  }

  // Handle warning notifications
  if (item.warning || notificationType.includes('warning') || notificationType.includes('warn')) {
    const warningMessage = item.warning || item.message || item.body;
    if (documentName) {
      return `${documentName} - ${warningMessage || 'Please review'}`;
    }
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} - ${warningMessage || 'Action required'}`;
    }
    if (warningMessage && !warningMessage.includes('Detected') && !warningMessage.includes('in ') && !warningMessage.includes('event in')) {
      return warningMessage;
    }
    return 'Please review';
  }

  // If there's a meaningful message (not generic), use it
  if (item.message && !item.message.includes('Detected') && !item.message.includes('was') && !item.message.includes('event in')) {
    return item.message;
  }

  // Build context-sensitive messages based on operation type and available data
  if (operationType === 'insert') {
    if (userName && documentName) {
      return `${userName} created ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} added a new ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `New ${collectionName}: ${documentName}`;
    }
    if (collectionName) {
      return `New ${collectionName} added`;
    }
    return 'New item created';
  }

  if (operationType === 'update') {
    if (userName && documentName) {
      return `${userName} updated ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} modified a ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `${documentName} was updated`;
    }
    if (collectionName) {
      return `A ${collectionName} was updated`;
    }
    return 'Item updated';
  }

  if (operationType === 'delete') {
    if (userName && documentName) {
      return `${userName} deleted ${documentName}`;
    }
    if (userName && collectionName) {
      return `${userName} removed a ${collectionName}`;
    }
    if (documentName && collectionName) {
      return `${documentName} was deleted`;
    }
    if (collectionName) {
      return `A ${collectionName} was deleted`;
    }
    return 'Item deleted';
  }

  // For job notifications
  if (item.type?.includes('complete') || item.type?.includes('success')) {
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} completed successfully`;
    }
    return 'Task completed';
  }

  if (item.type?.includes('failed') || item.type?.includes('error')) {
    if (item.jobName) {
      return `${parseByDelimiter(item.jobName)} failed`;
    }
    return 'Task failed';
  }

  // Fallback to generic message (avoid mentioning collection names)
  if (operationType) {
    const action = operationType === 'insert' ? 'created' : operationType === 'update' ? 'updated' : 'deleted';
    if (documentName) {
      return `${documentName} was ${action}`;
    }
    return `Item was ${action}`;
  }

  return item.body || item.message || 'New notification';
};

const NoticeItem = ({ item, height }) => {
  const theme = themeSelector();

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      style={[
        getModernCardStyle(theme, 'small'),
        {
          marginVertical: 6,
          marginHorizontal: 4,
          padding: 16,
          minHeight: 70,
        }
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        {/* Icon/Avatar Container */}
        <Animatable.View
          useNativeDriver
          animation={'slideInLeft'}
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: theme ? 'rgba(90, 192, 234, 0.2)' : 'rgba(90, 192, 234, 0.3)',
          }}
        >
          {item.postedBy ? (
            <Animatable.Image
              animation={'slideInLeft'}
              source={{ uri: item.postedBy.profile_information.avatar.img }}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                borderWidth: 1,
                borderColor: 'white',
              }}
            />
          ) : (
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              {returnIcon(item.operationType)}
            </View>
          )}
        </Animatable.View>

        {/* Content Container */}
        <View style={{ flex: 1, gap: 4 }}>
          <Animatable.Text
            numberOfLines={2}
            animation={'slideInDown'}
            style={{
              fontSize: 14,
              fontWeight: '600',
              color: getModernTextColor(theme, 'primary'),
              lineHeight: 18,
            }}
          >
            {getContextualMessage(item)}
          </Animatable.Text>

          <Text
            style={{
              fontSize: 12,
              color: getModernTextColor(theme, 'muted'),
              fontWeight: '500',
            }}
          >
            {getDaysAgo(item.wallTime || item.timestamp)}
          </Text>
        </View>
      </View>
    </Animatable.View>
  );
}
export default NoticeItem;